import type { Feature, Point } from 'geojson';

/** Propiedades del feature de etapa en el tileset. */
export type StageFeatureProperties = {
    /** Siempre presente: normalizeNumber lo indexa sin comprobar. */
    track_number: string;
    track_id?: string;
    route_id?: string;
    camino_id?: string;
    track_title?: string;
    name?: string;
    stroke?: string;
};
import type { Map as MapboxMap } from 'mapbox-gl';
import {dataTypes, featureTypes} from '../common/Constants';
import TrackData from './TrackData';
import type RouteData from './RouteData';
import {LngLat, Marker} from "mapbox-gl";
import MapService from "../common/MapService";
import AppService from "../common/AppService";

/** Item as returned by the stage list endpoint. */
export interface StageListItem {
    id: number | string;
    uri?: string;
    url?: string;
    name?: string;
}

/**
 * Object to store city from map marker.
 *
 * Example marker.
 * {
 id: some map id
 layer: map layer {id = "interactive-lines", type, source, source-layer}
 properties:
 - route_id: "camino-viejo"
 - camino_id: "camino-viejo"
 - name: "Etapa 21 Congosto - Cacabelos"
 - track_number: "21"
 - track_title: "Congosto - Cacabelos"
 - stroke: "#aa0000"
 type: Feature
 _geometry: Point{coordinates [lon, lat]}
 }
 */
class StageData extends TrackData {

    // Route Data. Lo asigna MapData al agrupar etapas por camino.
    routeData?: RouteData;

    // track_number lo declara TrackData: redeclararlo aqui volveria a definir
    // la propiedad como undefined tras super() (useDefineForClassFields).

    // Map Marker
    mapMarker?: Marker | null;

    // lat long coordinates
    firstCoordinate?: [number, number];

    // feature. Solo lo rellena fromMapFeature.
    feature?: Feature<Point, StageFeatureProperties>;

    /**
     * Returns hotel[id]
     */
    getMapItemId(): string | undefined {
        return this.track_id;
    }

    /**
     * Create from map feature.
     */
    static fromMapFeature(feature: Feature<Point, StageFeatureProperties>): StageData {
        const properties = feature.properties;
        // @todo Fix id
        const data = new StageData(dataTypes.stage);
        data.setFeature(feature);
        data.track_id = properties.track_id;
        data.route_id = properties.route_id;
        data.camino_id = properties.camino_id;
        data.track_title = properties.track_title;
        data.track_number = properties.track_number;

        data.track_number = this.normalizeNumber(properties.track_number);

        data.firstCoordinate = feature.geometry.coordinates as [number, number];

        data.feature = feature;

        return data;
    }

    /**
     * Create from data type, id
     */
    static fromListItem(item: StageListItem): StageData {
        const data = new StageData(dataTypes.stage, item.id);

        data.restUrl = item.uri;
        data.linkUrl = item.url;
        data.name = item.name;

        return data;
    }


    /**
     * Create DOM Element
     *
     * @param color Hex color stroke
     */
    createElement(color?: string): HTMLElement {
        if (!this.htmlElement) {
            const el = document.createElement('div');
            // Ambos valores pueden ser undefined. String() hace explicita la
            // conversion que el DOM ya aplicaba. @see FeatureData.createElement
            el.id = String(this.getMapItemId());
            el.innerHTML = String(this.track_number);
            el.classList.add('track-number');
            el.style.borderColor = String(color);
            this.htmlElement = el;
        }

        return this.htmlElement;
    }

    /**
     * Gets HTML element from document
     */
    getMapElement(refresh = true): HTMLElement | null | undefined {
        if (refresh || !this.htmlElement) {
            this.htmlElement = document.getElementById(String(this.getMapItemId()));
        }
        return this.htmlElement;
    }

    /**
     * Create MAP Marker.
     *
     * @param color Hex color stroke
     */
    createMapMarker(color?: string): Marker {
        if (!this.mapMarker) {
            const el = this.createElement(color);
            this.mapMarker = new Marker(el)
                .setLngLat(this.firstCoordinate!);

            this.mapMarker.getElement().addEventListener(AppService.getInteractiveEventType(), (event) => {
                event.stopPropagation();
                if (this.feature!.geometry.type !== 'Point')
                    return;
                const coordinates = this.firstCoordinate!;
                const longLat = new LngLat(coordinates[0], coordinates[1]);
                MapService.mapComponent.handleFeatureClick(featureTypes.track, this.feature, longLat);
            });

        }
        return this.mapMarker;
    }

    /**
     * Add marker to Map.
     *
     * @return true if successful.
     */
    addToMap(mapObject: MapboxMap): boolean {
        const marker = this.createMapMarker();
        if (marker) {
            marker.addTo(mapObject);
            return true;
        }
        else {
            return false;
        }
    }

    /**
     * Remove marker from map.
     */
    removeMarker(): void {
        if (this.mapMarker) {
            this.mapMarker.remove();
            this.mapMarker = null;
        }
        this.removeElement();
    }

    /**
     * Remove HTML element.
     */
    removeElement(): void {
        if (this.htmlElement) {
            this.htmlElement.remove();
            this.htmlElement = null;
        }
    }

    /**
     * remove first 0.
     */
    static normalizeNumber(trackNumber: string): string {
        if(trackNumber[0] === '0') {
            return trackNumber.substring(1);
        }
        return trackNumber;
    }
}

export default StageData;
